import { api } from '@/services/api/AxiosBaseService'

/**
 * Catálogo de propostas → Linhas e coberturas (área interna).
 *
 * Cada linha com as modalidades e as coberturas que oferece: texto do PDF,
 * carência, texto por modalidade e o peso de cada cobertura na tabela ativa.
 */

export interface NameHistoryEntry { name: string; until: string }

export interface CatalogTier {
  id: string
  name: string
  status: 'ATIVO' | 'INATIVO'
  beneficiaries: string
  minAge: number | null
  maxAge: number | null
  curve: string | null
  /** Preço cheio na tabela ativa (null = sem preço). */
  price: number | null
  nameHistory: NameHistoryEntry[]
}

export interface CatalogLineItem {
  id: string
  itemId: string
  name: string
  itemStatus: 'ATIVO' | 'INATIVO'
  /** Texto da cobertura nesta linha (vazio = usa o texto geral). */
  description: string | null
  generalText: string | null
  grace: string | null
  /** Securitária (seguro) nesta linha — aparece na proposta e no PDF. */
  insurance: boolean
  sortOrder: number
  nameHistory: NameHistoryEntry[]
  tierTexts: { tierId: string; text: string }[]
  /** Peso por modalidade na tabela ativa (0 a 1). */
  weights: { tierId: string; weight: number }[]
}

export interface CatalogLine {
  id: string
  name: string
  description: string | null
  status: 'ATIVO' | 'INATIVO'
  nameHistory: NameHistoryEntry[]
  tiers: CatalogTier[]
  items: CatalogLineItem[]
  available: { id: string; name: string }[]
  /** O que impede a linha de aparecer nas propostas. */
  problems: string[]
  offered: boolean
}

export interface CatalogMeta {
  activeTable: { id: string; version: number; name: string } | null
  proposalsByItem: { itemId: string; total: number }[]
}

const BASE = '/settings/commercial'

export const ProposalLinesService = {
  async list(): Promise<{ data: CatalogLine[]; meta: CatalogMeta }> {
    const r = await api.get<{ data: CatalogLine[]; meta: CatalogMeta }>(`${BASE}/proposal-lines`)
    return r.data
  },
  async addItem(lineId: string, itemId: string): Promise<void> {
    await api.post(`${BASE}/proposal-lines/${lineId}/items`, { itemId })
  },
  async updateItem(lineItemId: string, payload: { description: string | null; gracePeriod: string | null; tierTexts: { tierId: string; description: string }[] }): Promise<void> {
    await api.put(`${BASE}/proposal-line-items/${lineItemId}`, payload)
  },
  async setInsurance(lineItemId: string, isInsurance: boolean): Promise<void> {
    await api.put(`${BASE}/proposal-line-items/${lineItemId}/insurance`, { isInsurance })
  },
  async removeItem(lineItemId: string): Promise<void> {
    await api.delete(`${BASE}/proposal-line-items/${lineItemId}`)
  },
  async reorder(lineId: string, ids: string[]): Promise<void> {
    await api.put(`${BASE}/proposal-lines/${lineId}/order`, { ids })
  },
  async rename(type: 'line' | 'tier' | 'item', id: string, name: string): Promise<{ id: string; name: string; nameHistory: NameHistoryEntry[] }> {
    const r = await api.put<{ data: { id: string; name: string; nameHistory: NameHistoryEntry[] } }>(`${BASE}/proposal-catalog/rename`, { type, id, name })
    return r.data.data
  },
}
